import { Controller } from "stimulus";

const csrfToken = (): string => document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";

type SendReply = { status?: string; email_id?: string; reason?: string; error?: string };

export default class extends Controller {
  static targets = ["row", "totals", "send", "confirm", "confirmText", "table", "status"];
  static values = { url: String, sendsUrl: String, mailType: String, deliveryMode: String, max: Number, mailLabel: String, deliveryLabel: String };

  declare readonly rowTargets: HTMLTableRowElement[];
  declare readonly totalsTarget: HTMLElement;
  declare readonly sendTarget: HTMLButtonElement;
  declare readonly confirmTarget: HTMLElement;
  declare readonly confirmTextTarget: HTMLElement;
  declare readonly tableTarget: HTMLTableElement;
  declare readonly statusTarget: HTMLElement;
  declare readonly hasTableTarget: boolean;
  declare urlValue: string;
  declare sendsUrlValue: string;
  declare mailTypeValue: string;
  declare deliveryModeValue: string;
  declare maxValue: number;
  declare mailLabelValue: string;
  declare deliveryLabelValue: string;

  private gone = false;
  private loading = false;
  private running = false;
  private nextOffset: string | undefined;
  private total = 0;

  connect(): void {
    if (!this.hasTableTarget) return;
    const first = this.tableTarget.tBodies[0];
    this.nextOffset = first?.dataset.nextOffset || undefined;
    this.total = Number(first?.dataset.total || 0);
    this.refreshTotals();
    this.loadRest();
  }

  disconnect(): void {
    this.gone = true;
  }

  async loadRest(): Promise<void> {
    this.loading = true;
    while (this.nextOffset && !this.gone) {
      const offset = this.nextOffset;
      const url = new URL(this.urlValue, window.location.origin);
      url.searchParams.set("offset", offset);
      url.searchParams.set("rows_only", "1");
      try {
        const response = await fetch(url.toString(), { headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" }, credentials: "same-origin" });
        if (!response.ok) throw new Error(String(response.status));
        const template = document.createElement("template");
        template.innerHTML = (await response.text()).trim();
        const body = template.content.querySelector("tbody");
        if (!body || this.gone) return;
        this.tableTarget.appendChild(body);
        this.nextOffset = body.dataset.nextOffset || undefined;
        this.statusTarget.textContent = "";
      } catch {
        this.showRetry(Number(offset));
        break;
      } finally {
        this.refreshTotals();
      }
    }
    this.loading = false;
    this.refreshTotals();
  }

  askSend(): void {
    const count = this.chosen().length;
    this.confirmTextTarget.textContent = `Send ${count} ${this.mailLabelValue}${count === 1 ? "" : "s"} via ${this.deliveryLabelValue}?`;
    this.confirmTarget.hidden = false;
  }

  cancelSend(): void {
    this.confirmTarget.hidden = true;
  }

  async send(): Promise<void> {
    this.confirmTarget.hidden = true;
    const rows = this.chosen();
    this.running = true;
    this.refreshTotals();
    const tally = { sent: 0, skipped: 0, failed: 0 };
    for (const row of rows) {
      if (this.gone) return;
      this.mark(row, "contacted", "Sending", "");
      const reply = await this.sendOne(row);
      row.dataset.reachable = "false";
      if (reply.status === "sent") {
        tally.sent++;
        this.mark(row, "live", "Sent", reply.email_id || "");
      } else if (reply.status === "forbidden") {
        this.mark(row, "negative", "Not sent", reply.error || "");
        this.statusTarget.textContent = reply.error || "Access was removed; the run stopped.";
        break;
      } else if (reply.status === "error" || reply.status === "failed") {
        tally.failed++;
        this.mark(row, "negative", "Failed", reply.reason || reply.error || "");
      } else {
        tally.skipped++;
        this.mark(row, "paused", "Skipped", reply.reason || "");
      }
      this.statusTarget.textContent = `${tally.sent} sent · ${tally.skipped} skipped · ${tally.failed} failed`;
    }
    this.running = false;
    this.statusTarget.textContent = `Done: ${tally.sent} sent · ${tally.skipped} skipped · ${tally.failed} failed. Re-open the campaign to check the rest again.`;
    this.refreshTotals();
  }

  private async sendOne(row: HTMLTableRowElement): Promise<SendReply> {
    const body = new URLSearchParams({ bots_prospect_id: row.dataset.botsProspectId || "", mail_type: this.mailTypeValue, delivery_mode: this.deliveryModeValue });
    if (row.dataset.emailChainId) body.set("email_chain_id", row.dataset.emailChainId);
    try {
      const response = await fetch(this.sendsUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json", "X-Requested-With": "XMLHttpRequest", "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });
      const reply: SendReply = await response.json().catch(() => ({}));
      if (response.status === 403) return { status: "forbidden", error: reply.error };
      return reply.status ? reply : { status: "failed", reason: `HTTP ${response.status}` };
    } catch {
      return { status: "failed", reason: "The request didn't reach the server" };
    }
  }

  private chosen(): HTMLTableRowElement[] {
    return this.rowTargets.filter((row) => row.dataset.reachable === "true").slice(0, this.maxValue);
  }

  private mark(row: HTMLTableRowElement, pill: string, label: string, detail: string): void {
    const state = row.querySelector<HTMLElement>('[data-cell="state"]');
    const reasons = row.querySelector<HTMLElement>('[data-cell="reasons"]');
    if (state) state.innerHTML = `<span class="pill ${pill}"><span class="pdot"></span></span>`;
    state?.querySelector(".pill")?.appendChild(document.createTextNode(label));
    if (reasons && detail) {
      const key = document.createElement("span");
      key.className = "tt-key";
      key.textContent = detail;
      reasons.innerHTML = "";
      reasons.appendChild(key);
    }
  }

  private refreshTotals(): void {
    const checked = this.rowTargets.length;
    const reachable = this.rowTargets.filter((row) => row.dataset.reachable === "true").length;
    const pending = this.nextOffset ? ` · checking ${checked} of ${this.total}…` : ` · ${checked} checked`;
    this.totalsTarget.textContent = `${reachable} reachable · ${checked - reachable} can't receive${pending}`;
    const count = Math.min(reachable, this.maxValue);
    this.sendTarget.disabled = this.running || this.loading || count === 0;
    this.sendTarget.textContent = count > 0 ? `Send to the first ${count} reachable` : "Send to reachable";
  }

  private showRetry(offset: number): void {
    this.statusTarget.textContent = `Couldn't check rows ${offset + 1}–${Math.min(offset + 10, this.total)}. `;
    const retry = document.createElement("button");
    retry.type = "button";
    retry.className = "btn btn-sm btn-ghost";
    retry.textContent = "Retry";
    retry.addEventListener("click", () => { if (!this.loading) this.loadRest(); }, { once: true });
    this.statusTarget.appendChild(retry);
  }
}
